/**
 * Single source of truth for ClickHouse column type classification.
 *
 * Layered: `getTypeFromClickhouseToTS` classifies by type-name prefix, and every predicate below is
 * built on it — so a newly supported type is handled in one place and all consumers follow. Match on
 * a prefix rather than a list of names, so widths the app has not enumerated (`Int128`,
 * `DateTime64(9)`) classify correctly instead of silently falling through.
 *
 * Classify types by importing from here rather than re-deriving; this module depends on nothing else
 * in the app, so it is safe to import from any layer.
 */

export const DATE_ONLY_TYPES = ['Date', 'Nullable(Date)'] as const

export const DATE_TIME_TYPES = [
	'DateTime',
	'DateTime64(3)',
	'DateTime64(6)',
	'Nullable(DateTime)',
	'Nullable(DateTime64(3))',
	'Nullable(DateTime64(6))',
] as const

/**
 * Names the date types the UI is written against. Deliberately not used for classification — the
 * predicates below go through `getTypeFromClickhouseToTS` so an unenumerated width still classifies.
 */
export type DateTimeType = (typeof DATE_TIME_TYPES)[number] | (typeof DATE_ONLY_TYPES)[number]

const NULLABLE_PREFIX = 'Nullable('

export const isNullableType = (type: string): boolean => type.startsWith(NULLABLE_PREFIX)

export const unwrapNullable = (type: string): string =>
	isNullableType(type) ? type.slice(NULLABLE_PREFIX.length, -1) : type

/** The reduced union the UI reasons about. `Date` covers both date-only and date-time types. */
export type ClickhouseToTSTypes = 'int' | 'uint' | 'float' | 'string' | 'boolean' | DateTimeType | 'null'

/**
 * Anything unrecognised falls back to `string`, so callers that must tell "really a string" apart from
 * "unknown type" want `isStringFieldType`, which matches the String family exactly.
 */
export const getTypeFromClickhouseToTS = (clickhouseType: string): ClickhouseToTSTypes => {
	if (!clickhouseType) {
		return 'string'
	}
	const baseType = unwrapNullable(clickhouseType)

	if (baseType.startsWith('Int')) {
		return 'int'
	}
	if (baseType.startsWith('UInt')) {
		return 'uint'
	}
	if (baseType.startsWith('Float')) {
		return 'float'
	}
	if (baseType === 'Bool') {
		return 'boolean'
	}
	// One check covers `Date` and every `DateTime`/`DateTime64(n)` width.
	if (baseType.startsWith('Date')) {
		return 'Date'
	}
	return 'string'
}

const NUMERIC_TS_TYPES: readonly ClickhouseToTSTypes[] = ['int', 'uint', 'float']

/**
 * Numeric fields have high-cardinality values, so callers drop them from value pickers and search
 * columns. Absent/unknown type → false, so an unresolved schema keeps the column rather than hiding it.
 */
export const isNumericFieldType = (type: string | undefined): boolean =>
	NUMERIC_TS_TYPES.includes(getTypeFromClickhouseToTS(type ?? ''))

export const isBooleanFieldType = (type: string | undefined): boolean =>
	getTypeFromClickhouseToTS(type ?? '') === 'boolean'

/** Date-only *or* date-time. Use `isDateTimeType`/`isDateOnlyType` when the distinction matters. */
export const isAnyDateFieldType = (type: string | undefined): boolean =>
	getTypeFromClickhouseToTS(type ?? '') === 'Date'

/** `Date` is not parameterised, so an exact match on the unwrapped type is exhaustive here. */
export const isDateOnlyType = (type: string | undefined): boolean => unwrapNullable(type ?? '') === 'Date'

/**
 * Carries a time component, so excludes the date-only `Date`. Split off `isAnyDateFieldType` rather
 * than matched against `DATE_TIME_TYPES`, so a width that list does not enumerate — `DateTime64(9)`,
 * or any the backend adds later — is still recognised as a date-time.
 */
export const isDateTimeType = (type: string | undefined): boolean => isAnyDateFieldType(type) && !isDateOnlyType(type)

/** Exact, unlike `getTypeFromClickhouseToTS`, whose `string` doubles as the unrecognised-type fallback. */
export const isStringFieldType = (type: string | undefined): boolean => unwrapNullable(type ?? '') === 'String'
